import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

export default function Pagination({ totalCount, itemsPerPage = 10, onPageChange }) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const offset = (pageNumber - 1) * itemsPerPage;
    onPageChange(offset, itemsPerPage);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Handle case where totalPages is 0
    if (totalPages === 0) return null;

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <motion.button
          key={i}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handlePageChange(i)}
          className={`flex items-center justify-center w-9 h-9 text-sm font-bold rounded-xl transition-all duration-300 ${currentPage === i
            ? "bg-blue-700/80 dark:bg-blue-900/50 text-white shadow-[0_4px_12px_rgba(37,99,235,0.3)] dark:shadow-[0_4px_12px_rgba(59,130,246,0.3)] scale-110 active:scale-95"
            : "text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-white/10 hover:text-blue-600 dark:hover:text-blue-400 border border-transparent hover:border-gray-100 dark:hover:border-white/5 shadow-sm hover:shadow-md"
            }`}
        >
          {i}
        </motion.button>
      );
    }
    return pageNumbers;
  };

  return (
    <nav className="flex items-center justify-center pt-8 pb-4" aria-label="Table navigation">
      <div className="flex items-center gap-1 p-1 bg-gray-100/80 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[1.2rem] shadow-sm hover:shadow-md transition-shadow duration-300">
        <motion.button
          whileHover={currentPage > 1 ? { scale: 1.1 } : {}}
          whileTap={currentPage > 1 ? { scale: 0.9 } : {}}
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-300 ${currentPage === 1
            ? "text-gray-600 dark:text-gray-700 cursor-not-allowed opacity-20"
            : "text-gray-900 dark:text-gray-400 hover:bg-white dark:hover:bg-white/10 hover:text-blue-600 dark:hover:text-blue-400 shadow-sm hover:shadow-md"
            }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </motion.button>

        <div className="flex items-center gap-1.5 px-1">
          {renderPageNumbers()}
        </div>

        <motion.button
          whileHover={currentPage < totalPages ? { scale: 1.1 } : {}}
          whileTap={currentPage < totalPages ? { scale: 0.9 } : {}}
          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-300 ${currentPage === totalPages
            ? "text-gray-900 dark:text-gray-700 cursor-not-allowed opacity-20"
            : "text-gray-900 dark:text-gray-400 hover:bg-white dark:hover:bg-white/10 hover:text-blue-600 dark:hover:text-blue-400 shadow-sm hover:shadow-md"
            }`}
        >
          <ChevronRight className="w-5 h-5" />
        </motion.button>
      </div>
    </nav>
  );
}
